import React, { useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle, AlertCircle, HandHeart } from 'lucide-react';

/**
 * SponsorshipApplyCard — public form on the Impact page. Lets anyone with
 * adaptive needs (amputee, chronic-pain, etc.) apply for a sponsored
 * Enferno-tier account funded by other users' top-up donations.
 *
 * Anti-shame, anti-paperwork tone. No SLA promise. Backend endpoint:
 *   POST /api/sponsorship/apply  (no auth required)
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'amputee', label: 'Amputee' },
  { id: 'chronic-pain', label: 'Chronic pain' },
  { id: 'adaptive-needs', label: 'Other adaptive needs' },
  { id: 'other', label: "Doesn't quite fit — I'll explain" },
];

const SponsorshipApplyCard = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    category: 'amputee',
    reference_org: '',
    story: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    if (!form.name.trim() || !form.email.trim() || !form.story.trim() || form.story.trim().length < 10) {
      setError('Please fill in your name, email, and a few sentences of context (at least 10 characters).');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      await axios.post(`${API}/sponsorship/apply`, form);
      setStatus('success');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not submit. Try again in a moment.';
      setError(typeof msg === 'string' ? msg : 'Could not submit. Try again in a moment.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div
        className="bg-enf-surface-deep border border-adaptive-sage/40 rounded-xl p-8"
        data-testid="sponsorship-success"
      >
        <div className="flex items-start gap-4">
          <CheckCircle className="w-6 h-6 text-adaptive-sage flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-2xl tracking-wide mb-2">Application received.</h3>
            <p className="text-sm text-enf-text-secondary leading-relaxed mb-3">
              We don't gatekeep aggressively — the mission is access, not paperwork. We aim to respond while your request is still hot.
            </p>
            <p className="text-xs text-enf-text-muted">
              You'll hear back at <span className="text-enf-text-secondary font-semibold">{form.email}</span>. Reply to that email if anything changes in the meantime.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 md:p-8" data-testid="sponsorship-apply-card">
      <div className="flex items-start gap-3 mb-5">
        <HandHeart className="w-6 h-6 text-enf-accent-fg flex-shrink-0 mt-1" />
        <div>
          <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-1">Sponsored Access</div>
          <h3 className="font-display text-2xl md:text-3xl tracking-wide leading-tight mb-2">Apply for a Sponsored Account.</h3>
          <p className="text-sm text-enf-text-secondary leading-relaxed">
            Sponsored Enferno-tier accounts are funded by other users' optional top-up donations. If you have adaptive needs and the cost is a barrier, ask us — we don't gatekeep aggressively. The mission is access, not paperwork.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold block mb-1.5">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Your name"
              maxLength={120}
              className="w-full bg-enf-surface-mid border border-enf-surface-border focus:border-enf-role-info rounded-md px-3 py-2 text-sm text-enf-text-primary outline-none"
              data-testid="sponsorship-name"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold block mb-1.5">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-enf-surface-mid border border-enf-surface-border focus:border-enf-role-info rounded-md px-3 py-2 text-sm text-enf-text-primary outline-none"
              data-testid="sponsorship-email"
              required
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold block mb-1.5">Category</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="sponsorship-category-group">
            {CATEGORIES.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setField('category', c.id)}
                className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
                  form.category === c.id
                    ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                    : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
                }`}
                data-testid={`sponsorship-cat-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold block mb-1.5">
            Reference (optional) <span className="text-enf-text-muted normal-case">— clinic, VA, adaptive-sport org, anything</span>
          </label>
          <input
            type="text"
            value={form.reference_org}
            onChange={(e) => setField('reference_org', e.target.value)}
            placeholder="e.g. VA Hospital · CAF · Adaptive Training Foundation"
            maxLength={240}
            className="w-full bg-enf-surface-mid border border-enf-surface-border focus:border-enf-role-info rounded-md px-3 py-2 text-sm text-enf-text-primary outline-none"
            data-testid="sponsorship-reference"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold block mb-1.5">Your story</label>
          <textarea
            value={form.story}
            onChange={(e) => setField('story', e.target.value)}
            placeholder="A few sentences on your situation, what training looks like for you right now, and what an Enferno account would unlock. No essays needed."
            rows={5}
            maxLength={4000}
            className="w-full bg-enf-surface-mid border border-enf-surface-border focus:border-enf-role-info rounded-md px-3 py-2 text-sm text-enf-text-primary outline-none resize-y leading-relaxed"
            data-testid="sponsorship-story"
            required
          />
          <div className="text-[10px] text-enf-text-muted mt-1 text-right">{form.story.length} / 4000</div>
        </div>

        {error && (
          <div className="px-3 py-2 bg-enf-accent/10 border border-enf-accent-fg/30 text-enf-accent-fg text-xs rounded-md flex items-center gap-2">
            <AlertCircle className="w-3 h-3" /> {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-4 pt-2">
          <p className="text-xs text-enf-text-muted leading-relaxed flex-1">
            We aim to respond while your request is still hot.
          </p>
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors disabled:opacity-50 flex-shrink-0"
            data-testid="sponsorship-submit"
          >
            <Send className="w-4 h-4" />
            {status === 'submitting' ? 'Sending…' : 'Apply'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SponsorshipApplyCard;
